import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, 
  Upload, 
  Download, 
  Save, 
  Maximize2, 
  Minimize2, 
  Settings, 
  Copy, 
  RotateCcw,
  FileText,
  Zap,
  Shield,
  Smartphone,
  Globe,
  Code,
  CheckCircle,
  Eye,
  ChevronDown,
  AlertCircle,
  Info
} from 'lucide-react';

interface MinifierOptions {
  removeComments: boolean;
  removeWhitespace: boolean;
  removeEmptyElements: boolean;
  removeOptionalTags: boolean;
  removeRedundantAttributes: boolean;
  minimizeAttributes: boolean;
  removeScriptTypeAttributes: boolean;
  removeStyleLinkTypeAttributes: boolean;
  removeDefaultAttributes: boolean;
  minifyCSS: boolean;
  minifyJS: boolean;
  collapseWhitespace: boolean;
}

interface CodeIssue {
  line: number;
  type: 'info' | 'warning' | 'error';
  message: string;
}

const HTMLMinifier: React.FC = () => {
  const [inputHTML, setInputHTML] = useState<string>(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Advanced HTML Minifier Demo</title>
  
  <!-- CSS Styles -->
  <style type="text/css">
    body {
      margin: 0;
      padding: 20px;
      font-family: Arial, sans-serif;
      background-color: #f5f5f5;
    }
    
    .container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      padding: 30px;
      border-radius: 8px;
    }
    
    .header {
      text-align: center;
      margin-bottom: 30px;
    }
  </style>
</head>

<body>
  <!-- Main container -->
  <div class="container">
    <header class="header">
      <h1>Welcome to HTML Minifier</h1>
      <p>This is a demonstration of HTML minification capabilities.</p>
    </header>
    
    <main>
      <section class="content">
        <h2>Features</h2>
        <ul>
          <li>Remove unnecessary whitespace</li>
          <li>Remove HTML comments</li>
          <li>Optimize attributes</li>
          <li>Minify inline CSS and JavaScript</li>
        </ul>
        
        <div class="form-section">
          <div class="demo-form">
            <div class="form-group">
              <label for="name">Name:</label>
              <input type="text" id="name" name="name" required="" />
            </div>
            
            <div class="form-group">
              <label for="email">Email:</label>
              <input type="email" id="email" name="email" required="" />
            </div>
            
            <button type="button">Submit Demo</button>
          </div>
        </div>
      </section>
    </main>
    
    <footer>
      <p>&copy; 2024 HTML Minifier. All rights reserved.</p>
    </footer>
  </div>

  <!-- JavaScript -->
  <script type="text/javascript">
    document.addEventListener('DOMContentLoaded', function() {
      console.log('Page loaded successfully');
      
      // Form validation
      const form = document.querySelector('form');
      form.addEventListener('submit', function(e) {
        const name = document.getElementById('name').value;
        const email = document.getElementById('email').value;
        
        if (!name || !email) {
          e.preventDefault();
          alert('Please fill in all required fields');
        }
      });
    });
  </script>
</body>
</html>`);
  const [outputHTML, setOutputHTML] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'code' | 'preview' | 'split'>('split');
  const [compressionRatio, setCompressionRatio] = useState<number>(0);
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [minifiedSize, setMinifiedSize] = useState<number>(0);
  const [lineCount, setLineCount] = useState<number>(0);
  const [charCount, setCharCount] = useState<number>(0);
  const [wordCount, setWordCount] = useState<number>(0);
  const [codeIssues, setCodeIssues] = useState<CodeIssue[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<MinifierOptions>({
    removeComments: true,
    removeWhitespace: true,
    removeEmptyElements: true,
    removeOptionalTags: false,
    removeRedundantAttributes: true,
    minimizeAttributes: true,
    removeScriptTypeAttributes: true,
    removeStyleLinkTypeAttributes: true,
    removeDefaultAttributes: true,
    minifyCSS: true,
    minifyJS: true,
    collapseWhitespace: true
  });

  useEffect(() => {
    const lines = inputHTML.split('\n').length;
    const chars = inputHTML.length;
    const words = inputHTML.trim() ? inputHTML.trim().split(/\s+/).length : 0;
    
    setLineCount(lines);
    setCharCount(chars);
    setWordCount(words);
    
    // Analyze code issues
    const issues: CodeIssue[] = [];
    const htmlLines = inputHTML.split('\n');
    
    htmlLines.forEach((line, index) => {
      if (line.includes('type="text/css"') && line.includes('<style')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Consider removing type="text/css" from style tags (HTML5 default)'
        });
      }
      if (line.includes('type="text/javascript"') && line.includes('<script')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Consider removing type="text/javascript" from script tags (HTML5 default)'
        });
      }
      if (line.includes('required=""')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Boolean attribute "required" can be minimized'
        });
      }
      if (line.match(/\s{2,}/)) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'Multiple consecutive spaces detected'
        });
      }
      if (line.includes('<!-- ') && !line.includes('<!--[if')) {
        issues.push({
          line: index + 1,
          type: 'info',
          message: 'HTML comment found - consider removing for production'
        });
      }
    });
    
    setCodeIssues(issues);
  }, [inputHTML]);

  const minifyHTML = (html: string): string => {
    let minified = html;

    if (options.removeComments) {
      // Remove HTML comments but preserve conditional comments
      minified = minified.replace(/<!--(?!\[if\s)[\s\S]*?-->/g, '');
    }

    if (options.removeScriptTypeAttributes) {
      minified = minified.replace(/(<script[^>]*)\s+type=['"]text\/javascript['"]([^>]*>)/gi, '$1$2');
    }

    if (options.removeStyleLinkTypeAttributes) {
      minified = minified.replace(/(<style[^>]*)\s+type=['"]text\/css['"]([^>]*>)/gi, '$1$2');
      minified = minified.replace(/(<link[^>]*)\s+type=['"]text\/css['"]([^>]*>)/gi, '$1$2');
    }

    if (options.removeDefaultAttributes) {
      minified = minified.replace(/\s+method=['"]get['"](\s|>)/gi, '$1');
      minified = minified.replace(/\s+target=['"]_self['"](\s|>)/gi, '$1');
    }

    if (options.minimizeAttributes) {
      // Minimize boolean attributes
      minified = minified.replace(/\s+(checked|selected|disabled|readonly|multiple|autofocus|autoplay|controls|defer|hidden|loop|open|required|reversed)=['"][^'"]*['"]/gi, ' $1');
      minified = minified.replace(/\s+(checked|selected|disabled|readonly|multiple|autofocus|autoplay|controls|defer|hidden|loop|open|required|reversed)=['"]['"]?/gi, ' $1');
    }

    if (options.removeRedundantAttributes) {
      // Remove redundant attributes
      minified = minified.replace(/\s+autocomplete=['"]on['"](\s|>)/gi, '$1');
      minified = minified.replace(/\s+spellcheck=['"]true['"](\s|>)/gi, '$1');
    }

    if (options.removeEmptyElements) {
      // Remove empty elements (but preserve some that might be needed)
      minified = minified.replace(/<p[^>]*>\s*<\/p>/gi, '');
      minified = minified.replace(/<span[^>]*>\s*<\/span>/gi, '');
      minified = minified.replace(/<div[^>]*>\s*<\/div>/gi, '');
    }

    if (options.minifyCSS) {
      // Basic CSS minification within style tags
      minified = minified.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
        let minifiedCSS = css
          .replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .replace(/\s*([{}();,=+\-*/<>!&|])\s*/g, '$1') // Remove spaces around operators
          .replace(/;\s*}/g, '}') // Remove semicolon before closing brace
          .trim();
        return match.replace(css, minifiedCSS);
      });
    }

    if (options.minifyJS) {
      // Basic JavaScript minification within script tags
      minified = minified.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, (match, js) => {
        let minifiedJS = js
          .replace(/\/\/.*$/gm, '') // Remove single-line comments
          .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multi-line comments
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .replace(/\s*([{}();,=+\-*/<>!&|])\s*/g, '$1') // Remove spaces around operators
          .trim();
        return match.replace(js, minifiedJS);
      });
    }

    if (options.collapseWhitespace) {
      // Collapse whitespace between tags
      minified = minified.replace(/>\s+</g, '><');
      // Remove leading and trailing whitespace on lines
      minified = minified.replace(/^\s+|\s+$/gm, '');
      // Replace multiple spaces with single space
      minified = minified.replace(/\s{2,}/g, ' ');
    }

    if (options.removeWhitespace) {
      // More aggressive whitespace removal
      minified = minified.replace(/\n\s*\n/g, '\n'); // Remove empty lines
      minified = minified.replace(/\s*\n\s*/g, ' '); // Replace newlines with spaces
    }

    return minified.trim();
  };

  const handleMinify = (): void => {
    const minified = minifyHTML(inputHTML);
    setOutputHTML(minified);
    
    const originalBytes = new Blob([inputHTML]).size;
    const minifiedBytes = new Blob([minified]).size;
    const ratio = originalBytes > 0 ? ((originalBytes - minifiedBytes) / originalBytes * 100) : 0;
    
    setOriginalSize(originalBytes);
    setMinifiedSize(minifiedBytes);
    setCompressionRatio(Math.round(ratio * 100) / 100);
  };

  const handleUpload = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'text/html' || file.name.endsWith('.html') || file.name.endsWith('.htm'))) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputHTML(content);
      };
      reader.readAsText(file);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([outputHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'minified.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSave = (): void => {
    localStorage.setItem('advanced-html-minifier-input', inputHTML);
    localStorage.setItem('advanced-html-minifier-output', outputHTML);
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputHTML);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleReset = (): void => {
    setInputHTML('');
    setOutputHTML('');
    setCompressionRatio(0);
    setOriginalSize(0);
    setMinifiedSize(0);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`min-h-screen bg-gray-900 text-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Top Navigation */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-2">
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <span>CATEGORY</span>
          <ChevronDown className="w-3 h-3 rotate-90" />
          <span>Minifiers</span>
          <ChevronDown className="w-3 h-3 rotate-90" />
          <span className="text-blue-400">HTML Minifier</span>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Globe className="w-6 h-6 text-orange-400" />
              <h1 className="text-xl font-semibold text-white">Advanced HTML Minifier</h1>
            </div>
            
            <div className="flex items-center bg-gray-800 rounded-lg border border-gray-700">
              <button
                onClick={() => setViewMode('code')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  viewMode === 'code' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Code
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'preview' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Preview
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  viewMode === 'split' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Split
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleMinify}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors font-medium"
            >
              <Play className="w-4 h-4" />
              <span>Run</span>
            </button>
            
            <button
              onClick={handleUpload}
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
              title="Upload HTML file"
            >
              <Upload className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleDownload}
              disabled={!outputHTML}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              title="Download minified HTML"
            >
              <Download className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleCopy}
              disabled={!outputHTML}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleSave}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Save to local storage"
            >
              <Save className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleReset}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Reset all"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            
            <button
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Toggle fullscreen"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Code Issues */}
      {codeIssues.length > 0 && (
        <div className="bg-gray-900 border-b border-gray-700 px-6 py-3">
          <div className="flex items-center space-x-2 text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Code Issues ({codeIssues.length})</span>
          </div>
          <div className="mt-2 space-y-1">
            {codeIssues.slice(0, 3).map((issue, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <span className={`text-xs px-2 py-1 rounded ${
                  issue.type === 'warning' ? 'bg-yellow-600' : 'bg-blue-600'
                }`}>
                  {issue.type}
                </span>
                <span className="text-gray-400">Line {issue.line}:</span>
                <span className="text-gray-300">{issue.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor Status Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <ChevronDown className="w-4 h-4" />
              <span className="text-white">HTML Editor</span>
              <span className="text-green-400">✓ Auto-saved</span>
            </div>
            <span>{lineCount} lines</span>
            <span>{charCount} chars</span>
            <span>{wordCount} words</span>
            {compressionRatio > 0 && (
              <>
                <span>Original: {formatBytes(originalSize)}</span>
                <span>Minified: {formatBytes(minifiedSize)}</span>
                <span className="text-green-400">Saved: {compressionRatio}%</span>
              </>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <span>Live Preview</span>
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-green-400">Live</span>
          </div>
        </div>
      </div>

      {/* Minification Options - Fixed Position */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-medium text-white">Minification Options:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.entries(options).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setOptions(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-gray-300">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>Secure Processing</span>
            </div>
            <div className="flex items-center space-x-2">
              <Zap className="w-4 h-4" />
              <span>Real-time</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex" style={{ height: 'calc(100vh - 280px)' }}>
        {/* Left Pane - Input */}
        {(viewMode === 'code' || viewMode === 'split') && (
          <div className="flex-1 flex flex-col bg-gray-900 border-r border-gray-700">
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex">
                {/* Line numbers */}
                <div className="w-12 bg-gray-800 border-r border-gray-700 text-right py-4 text-sm text-gray-500 font-mono leading-6 overflow-hidden">
                  {inputHTML.split('\n').map((_, index) => (
                    <div key={index} className="px-2 h-6">
                      {index + 1}
                    </div>
                  ))}
                </div>
                
                {/* Code editor */}
                <div className="flex-1">
                  <textarea
                    value={inputHTML}
                    onChange={(e) => setInputHTML(e.target.value)}
                    className="w-full h-full bg-transparent text-white font-mono text-sm leading-6 p-4 resize-none focus:outline-none"
                    placeholder="Enter your HTML code here..."
                    spellCheck={false}
                    style={{ 
                      tabSize: 2,
                      fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right Pane - Output/Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className="flex-1 flex flex-col bg-gray-900">
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex">
                {/* Line numbers */}
                <div className="w-12 bg-gray-800 border-r border-gray-700 text-right py-4 text-sm text-gray-500 font-mono leading-6 overflow-hidden">
                  {outputHTML.split('\n').map((_, index) => (
                    <div key={index} className="px-2 h-6">
                      {index + 1}
                    </div>
                  ))}
                </div>
                
                {/* Output display */}
                <div className="flex-1">
                  <pre className="w-full h-full bg-transparent text-gray-300 font-mono text-sm leading-6 p-4 overflow-auto whitespace-pre-wrap break-words">
                    {outputHTML || '<!-- Minified HTML will appear here after running the minifier... -->'}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".html,.htm,text/html"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default HTMLMinifier;